import React, { useEffect, useState } from "react";
import "./App.scss";
import SiteSummary from "./components/SiteSummary/SiteSummary";
import Layout from "./components/common/Layout/Layout";
import PageNotFound from "./components/common/PageNotFound/PageNotFound";
import { Route, Routes, BrowserRouter, useLocation } from "react-router-dom";
import AssetDetails from "./components/AssetDetails/AssetDetails";
import { WellsProvider } from "./contexts/WellsContext";
import Spinner from "./components/common/Spinner/Spinner";
import set from 'lodash/set';

function App() {
  // Remove 'ref' param from URL after login on any page
  function RefCleanup() {
    const location = useLocation();
    useEffect(() => {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('ref')) {
        searchParams.delete('ref');
        const newSearch = searchParams.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, '', newUrl);
      }
    }, [location]);
    return null;
  }
  // Helper to get cookie value
  function getCookie(name: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
  }

  // Helper to set cookie (24 hours expiry for last_path)
  function setCookie(name: string, value: string, days = 1) {
    // Only last_path uses 24h, others can use days param if needed
    if (name === 'last_path') {
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=86400`;
    } else {
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${days * 24 * 60 * 60}`;
    }
  }

  // Store last path (after base URL) except for landing/login/logout, using react-router location
  function LastPathUpdater() {

    const location = useLocation();

    useEffect(() => {

      const currentPath =
        location.pathname + location.search;

      const skipPaths = [
        "/Account/Login",
        "/Account/Logout"
      ];

      // Never save login token urls
      const hasRefToken =
        location.search.includes("ref=");

      const isJwtAccessTokenExist =
        localStorage.getItem("jwtAccessToken");

      if (
        !skipPaths.includes(location.pathname) &&
        !hasRefToken &&
        isJwtAccessTokenExist
      ) {
        setCookie("last_path", currentPath);
      }

    }, [location]);

    return null;
  }
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false); // Initially set to null
  const [loading, setLoading] = useState(true);
  const [isUserAuthorized, setIsUserAuthorized] = useState(false)

  const handleLogout = () => {

    const currentPath =
      window.location.pathname + window.location.search;

    // Save current route before logout
    if (!window.location.search.includes("ref=")) {
      setCookie("last_path", currentPath);
    }

    setIsUserLoggedIn(false);
    setIsUserAuthorized(false);

    localStorage.clear();
    sessionStorage.clear();

    window.location.href =
      process.env.REACT_APP_LOGOUT_URL;
  }

  const handleRedirect = () => {
    window.location.href = process.env.REACT_APP_LOGOUT_URL
    localStorage.clear()
  }

  useEffect(() => {
    // Define an async function inside the useEffect
    const fetchData = async () => {
      const currentUrl = window.location.href;
      const searchParams = new URLSearchParams(window.location.search);
      const refParam = searchParams.get("ref");
      const paramsObj = {};
      searchParams.forEach((value, key) => {
        set(paramsObj, key, value); // Safely assign the values
      });

      const isJwtAccessTokenExist = localStorage.getItem("jwtAccessToken");

      if (isJwtAccessTokenExist === null) {
        if (!isUserLoggedIn) { // Check if login status is not determined yet
          if ('ref' in paramsObj) {
            localStorage.clear();
            const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;

            const response = await fetch(
              `${baseUrl}/Account/GetAccessToken`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json"
                },
                body: JSON.stringify({
                  referenceToken: paramsObj["ref"] as string
                })
              }
            );

            if (!response.ok || response.status !== 200) {
              setIsUserLoggedIn(false)
              setIsUserAuthorized(false)
              setTimeout(() => { handleRedirect() }, 3000)
              return
            }
            const data = await response.json();

            if (data?.access_token && data?.refresh_token) {
              localStorage.setItem("jwtUserReadOnly", data?.readOnly?.toString());
              localStorage.setItem("jwtUserName", data?.userName);
              localStorage.setItem("jwtUserEmail", data?.email);
              localStorage.setItem("jwtAccessToken", data?.access_token);
              localStorage.setItem("jwtRefreshToken", data?.refresh_token);
              localStorage.setItem("jwtIsGroupAdmin", data?.isGlobalAdmin);
              localStorage.setItem("jwtIsValveControlUser", data?.isValveControlUser);
              localStorage.setItem("jwtIsPlLimitedUser", data?.isPlLimitedAccessUser);
              setIsUserAuthorized(true);
              setIsUserLoggedIn(true);
            }
          } else {
            if (process.env.REACT_APP_REDIRECT_URL !== undefined) {

              const lastPath = getCookie("last_path");
              const landingPath = "/";

              // IMPORTANT: use host instead of origin
              let redirectAppUrl =
                `${window.location.host}${window.location.pathname}${window.location.search}`;

              if (lastPath && lastPath !== landingPath) {
                redirectAppUrl = `${window.location.host}${lastPath}`;
              }

              const baseLoginUrl = process.env.REACT_APP_REDIRECT_URL || '';

              // Remove existing app param if present
              const loginBasePath = baseLoginUrl.split('?')[0];

              // Build URL
              const loginUrl =
                `${loginBasePath}?app=${redirectAppUrl}`;

              window.location.href = loginUrl;

              setLoading(false);
              return;
            }
          }
        }
      } else {
        setIsUserLoggedIn(true);
        setIsUserAuthorized(true);
      }

      setLoading(false);
      // Define a variable to hold the timeout ID
      let timeoutId;

      // Set the timeout: remove only the 'ref' param and preserve any other query params
      timeoutId = setTimeout(() => {
        searchParams.delete("ref");
        const newSearch = searchParams.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, "", newUrl);

        // Clear the timeout after it executes for the first time
        clearTimeout(timeoutId);
      }, 3000);

      // Cleanup function to clear the timeout if the component unmounts
      return () => clearTimeout(timeoutId);
    };

    // Call the async function immediately
    fetchData();
  }, []);


  useEffect(() => {

    const refreshTokenInterval = 20 * 60 * 1000; // Refresh token after every 20 minutes (in milliseconds)
    const interval = setInterval(async () => {

      const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;

      const response = await fetch(
        `${baseUrl}/Account/GetRefreshToken`,
        {
          method: "POST",
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            refreshToken: localStorage.getItem("jwtRefreshToken")
          })
        }
      );

      if (!response.ok || response.status !== 200) {
        setIsUserLoggedIn(false)
        setIsUserAuthorized(false)
        setTimeout(() => { handleRedirect() }, 3000)
        return
      }
      const data = await response.json();

      if (data?.access_token && data?.refresh_token) {
        localStorage.setItem("jwtAccessToken", data?.access_token);
        localStorage.setItem("jwtRefreshToken", data?.refresh_token);
        setIsUserAuthorized(true);
        setIsUserLoggedIn(true);
      }

    }, refreshTokenInterval);

    return () => clearInterval(interval);
  }, []);


  if (loading) {
    return <Spinner />;
  }

  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
      <RefCleanup />
      <LastPathUpdater />
      <WellsProvider>
        <Routes>
          {isUserLoggedIn && isUserAuthorized ? (
            <Route path="/" element={<Layout />}>
              {/* Default route */}
              <Route index element={<SiteSummary handleLogout={handleLogout} />} />

              {/* Child routes */}
              <Route
                path="assetdetails/*"
                element={<AssetDetails handleLogout={handleLogout} />}
              />

              {/* 404 inside layout */}
              <Route path="*" element={<PageNotFound />} />
            </Route>
          ) : (
            /* If not authorized */
            <Route path="*" element={<PageNotFound />} />
          )}
        </Routes>
      </WellsProvider>
    </BrowserRouter>
  );
}

export default App;
